/**
 * Chapter 6, culture and conversation: trends (11), what people say (35), the
 * moments market by market (46), a moment on social (52).
 */
import type { CulturalMoment, MomentConfidence } from "@/types/strategyDocument.type";
import {
  Eyebrow, Flag, Foot, Headline, Key, Picture, Slide, SourceLine, V,
  accountName, countryName, dayLong, dayShort, num, rangeLong, slideText,
  type TemplateContext,
} from "./kit";

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* ── 11 Trends ────────────────────────────────────────────────────────── */

export function TrendsSlide({ ctx }: { ctx: TemplateContext }) {
  const culture = ctx.doc.culture!;
  const indexes = ctx.slide.template.id === "trends" ? ctx.slide.template.trends : [];
  const t = slideText(ctx);
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What is *moving now.*" size={54} lineHeight={60} />
        <div className="sv-wall">
          {indexes.map((i) => {
            const tr = culture.trends[i];
            const example = tr.examples.find((e) => e.picture_url);
            return (
              <div key={tr.id} className="sv-wall-frame">
                <Picture ctx={ctx} slot={`trend:${tr.trend_id ?? tr.id}`} label={tr.name} url={example?.picture_url} className="sv-wall-pic" />
                <V as="p" className="sv-wall-head">{tr.name}</V>
                <p className="sv-wall-meta">
                  <V>{capitalise(tr.kind)}</V> · <V>{tr.stage}</V>
                  {tr.first_seen_at ? <> · first seen <V>{dayShort(tr.first_seen_at)}</V></> : null}
                  {tr.last_observed_at ? <>, last seen <V>{dayShort(tr.last_observed_at)}</V></> : null}
                  {tr.brand_fit?.line ? <> · <V>{tr.brand_fit.line}</V></> : null}
                </p>
              </div>
            );
          })}
        </div>
        <Foot ctx={ctx} extra=" · Creo Trends, stage and dates as observed" />
      </div>
    </Slide>
  );
}

/* ── 35 What people say ───────────────────────────────────────────────── */

const short = (url?: string | null) => (url ? `${url.replace(/^https?:\/\/(www\.)?/, "")}…` : null);

export function ConversationSlide({ ctx }: { ctx: TemplateContext }) {
  const culture = ctx.doc.culture!;
  const conversation = culture.conversation!;
  const t = slideText(ctx);
  const sample = conversation.sample;
  const mood = conversation.mood;
  const tagged = culture.posts_about_brand.slice(0, 2);
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What people say." lineHeight={57} className="sv-claim-tight" />
        {sample?.comments ? (
          <p className="sv-conv-meaning">Themes counted over <V>{sample.comments.toLocaleString("en-US")}</V> comments on the set’s posts{sample.window_start ? <>, <V>{rangeLong(sample.window_start, sample.window_end)}</V></> : null}. Every quote links to its comment.</p>
        ) : null}
        <div className="sv-conv-body">
          <div className="sv-themes">
            {conversation.themes.slice(0, 3).map((th) => (
              <div key={th.id} className="sv-theme">
                <div className="sv-theme-head">
                  <V as="p" className="sv-theme-name">{th.theme}</V>
                  <span className="sv-push" />
                  <p className="sv-theme-count"><V>{th.count.toLocaleString("en-US")}</V> comments · <V>{th.share}%</V>{th === conversation.themes[0] ? " of the sample" : ""}{th.mood ? <> · <V>{th.mood}</V></> : null}</p>
                </div>
                <div className="sv-theme-volume"><i style={{ width: `${Math.min(100, th.share * 2)}%` }} /></div>
                {th.quote?.excerpt ? <V as="p" className="sv-theme-quote" dir="auto">“{th.quote.excerpt.replace(/^[“"]|[”"]$/g, "")}”</V> : null}
                {th.quote ? (
                  <p className="sv-theme-source">
                    {short(th.quote.url) ? <V>{short(th.quote.url)}</V> : null}
                    {th.quote.posted_at ? <> · <V>{dayShort(th.quote.posted_at)}</V></> : null}
                    {th.quote.translation ? <> · <V>“{th.quote.translation}”</V></> : null}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
          {tagged.length ? (
            <div className="sv-examples">
              {tagged.map((p, i) => (
                <div key={i} className="sv-example">
                  <Picture ctx={ctx} label={`A post by @${p.author ?? ""}`} url={p.picture_url} className="sv-example-pic" />
                  <p className="sv-example-line">
                    {p.author ? <V>@{p.author.replace(/^@/, "")}</V> : null}
                    {p.note ? <> · <V>{p.note}</V></> : null}
                    {p.measure_line ? <> · <V>{p.measure_line}</V></> : null}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        {mood ? (
          <div className="sv-mood">
            <p className="sv-mood-key">How they feel about the category · <V>{mood.sample_size.toLocaleString("en-US")}</V> comments read</p>
            <div className="sv-mood-bar">
              <i style={{ flexGrow: mood.positive }} />
              <i style={{ flexGrow: mood.neutral }} />
              <i style={{ flexGrow: mood.negative }} />
            </div>
            <div className="sv-mood-labels">
              <div style={{ flexGrow: mood.positive }}><V as="b">{mood.positive}%</V><p>positive{mood.positive_line ? <>: <V>{mood.positive_line}</V></> : null}</p></div>
              <div style={{ flexGrow: mood.neutral }}><V as="b">{mood.neutral}%</V><p>neutral{mood.neutral_line ? <>: <V>{mood.neutral_line}</V></> : null}</p></div>
              <div style={{ flexGrow: mood.negative }}><V as="b">{mood.negative}%</V></div>
            </div>
          </div>
        ) : null}
        <Foot ctx={ctx} flag={sample?.read_at ? (
          <p className="sv-checked">
            <svg aria-hidden viewBox="0 0 12 12" width="12" height="12"><path d="M2 6.3 4.6 9 10 3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Public comments, read <V>{dayLong(sample.read_at)}</V>
          </p>
        ) : null} />
      </div>
    </Slide>
  );
}

/* ── 46 The moments, market by market ─────────────────────────────────── */

const CONFIDENCE: Record<MomentConfidence, string> = { fixed: "Fixed", moon_sighting: "Moon-sighting", estimate: "Estimate" };

/** "8 Feb to 9 Mar", "10 to 12 Mar", "22 Feb", or what the strategist wrote. */
export function momentDates(m: { date_range: { start?: string | null; end?: string | null }; date_label?: string | null }): string {
  if (m.date_label) return m.date_label;
  const { start, end } = m.date_range;
  if (!start) return "";
  if (!end || end.slice(0, 10) === start.slice(0, 10)) return dayShort(start);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${Number(start.slice(8, 10))} to ${dayShort(end)}`;
  return `${dayShort(start)} to ${dayShort(end)}`;
}

function MomentTile({ m, dark }: { m: CulturalMoment; dark?: boolean }) {
  return (
    <div className={`sv-moment${dark ? " sv-moment-dark" : ""}`}>
      <V as="p" className="sv-moment-name">{m.moment}</V>
      <p className="sv-moment-when"><V>{momentDates(m)}</V><span className="sv-moment-pill" data-confidence={m.date_confidence}>{CONFIDENCE[m.date_confidence]}</span></p>
      {m.sensitivities ? <V as="p" className="sv-moment-text">{m.sensitivities}</V> : null}
      <span className="sv-push" />
      {m.evidence?.note || m.evidence?.read_at ? <V as="p" className="sv-moment-evidence">{m.evidence.note ?? `Read ${dayShort(m.evidence.read_at)}`}</V> : null}
    </div>
  );
}

const WORDS = ["", "One moment", "Two moments", "Three moments", "Four moments", "Five moments", "Six moments"];

export function MomentsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const moments = doc.culture!.cultural_moments;
  const t = slideText(ctx);
  const markets = (doc.brief?.markets ?? []).map((m) => m.country);
  const both = moments.filter((m) => !m.markets.length || markets.every((c) => m.markets.includes(c)));
  const own = markets.map((c) => ({ c, list: moments.filter((m) => m.markets.length && !markets.every((x) => m.markets.includes(x)) && m.markets.includes(c)) })).filter((g) => g.list.length);
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The moments,\n*market by market.*" />
        <div className="sv-moments">
          {both.length ? (
            <div className="sv-moments-both">
              <div className="sv-moments-head">
                <span className="sv-flags">{markets.map((c) => <Flag key={c} code={c} size={11} />)}</span>
                <p className="sv-key sv-key-lime">{markets.length > 1 ? "Both markets" : "The market"}</p>
                <p className="sv-moments-sub">{WORDS[both.length] ?? `${both.length} moments`} the whole audience lives by</p>
              </div>
              <div className="sv-moments-row">
                {both.slice(0, 5).map((m, i) => (
                  <div key={m.id} className="sv-moments-cell">{i ? <i className="sv-moments-divider" /> : null}<MomentTile m={m} dark /></div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="sv-moments-markets">
            {own.slice(0, 2).map((g) => (
              <div key={g.c} className="sv-moments-market">
                <p className="sv-moments-head"><Flag code={g.c} size={11} /><span className="sv-key sv-key-ink">{countryName(g.c)} only</span></p>
                <div className="sv-moments-cards">
                  {g.list.slice(0, 3).map((m) => <MomentTile key={m.id} m={m} />)}
                </div>
              </div>
            ))}
          </div>
          <SourceLine className="sv-source-plain">Dates: Hijri dates calculated, within a day of the moon sighting, and official notices{moments.find((m) => m.evidence?.read_at) ? <>, read <V>{dayLong(moments.find((m) => m.evidence?.read_at)!.evidence!.read_at)}</V></> : null}. Sensitivities: the brand’s guidelines and public guidance.</SourceLine>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 52 A moment on social ────────────────────────────────────────────── */

export function MomentOnSocialSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const m = doc.culture!.cultural_moments[ctx.slide.template.id === "moment" ? ctx.slide.template.moment : 0];
  const social = m.on_social!;
  const t = slideText(ctx);
  const sample = social.sample;
  const topShare = Math.max(...social.people_post.map((p) => p.share), 0);
  const worked = social.what_worked;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={`${m.moment} *on social.*`} />
        <div className="sv-onsocial">
          <p className="sv-onsocial-window">
            <span className="sv-pill-dark">Last year’s window</span>
            <span><V>{rangeLong(social.window.start, social.window.end)}{social.window.end ? ` ${social.window.end.slice(0, 4)}` : ""}</V>{sample?.posts ? <> · <V>{sample.posts.toLocaleString("en-US")}</V> posts{sample.accounts ? <> from <V>{sample.accounts}</V> accounts</> : null}</> : null}{sample?.read_at ? <>, read <V>{dayLong(sample.read_at)}</V></> : null}</span>
          </p>
          <div className="sv-onsocial-parts">
            <div className="sv-onsocial-col">
              <Key className="sv-key-ink">What people post</Key>
              {social.people_post.length ? social.people_post.slice(0, 5).map((p) => (
                <div key={p.topic} className="sv-onsocial-topic">
                  <p><V>{p.topic}</V><span className="sv-push" /><V as="b">{p.share}%</V></p>
                  <i data-top={p.share === topShare || undefined} style={{ width: Math.round(p.share * 9.68) }} />
                </div>
              )) : <p className="sv-open">{social.people_post_source?.what_would_settle_it ?? "Open: people’s posts in that window are not read yet."}</p>}
            </div>
            <div className="sv-onsocial-col">
              <Key className="sv-key-ink">What brands do</Key>
              {social.brands_do.slice(0, 4).map((b) => (
                <div key={b.account} className="sv-onsocial-brand">
                  <p><V as="b">{accountName(doc, ctx.brand, b.account)}</V>{b.posts != null ? <span><V>{b.posts}</V> posts</span> : null}</p>
                  <V as="p">{b.what}</V>
                </div>
              ))}
              {social.trends.length ? (
                <div className="sv-onsocial-trends">
                  <Key className="sv-key-ink">The trends in that window</Key>
                  {social.trends.slice(0, 3).map((tr) => (
                    <p key={tr.name}><V>{tr.name}</V><span className="sv-push" /><span className="sv-pill-dark" data-stage={tr.stage}><V>{tr.stage_line ?? tr.stage}</V></span></p>
                  ))}
                </div>
              ) : null}
            </div>
            {worked ? (
              <div className="sv-worked">
                <p className="sv-key sv-key-lime">What worked</p>
                {worked.ratio_to_median != null ? <p className="sv-worked-figure"><V as="b">{worked.ratio_to_median}×</V><span>the usual</span></p> : null}
                {worked.line ? <V as="p" className="sv-worked-line">{worked.line}</V> : null}
                <div className="sv-worked-posts">
                  {worked.best_posts.slice(0, 3).map((p, i) => (
                    <div key={i} className="sv-worked-post">
                      <Picture ctx={ctx} label={`A post by ${p.author ?? ""}`} url={p.picture_url} />
                      {num(p.value) ? <span>▶ <V>{num(p.value)}</V></span> : null}
                    </div>
                  ))}
                </div>
                <V as="p" className="sv-worked-who">{worked.best_posts.slice(0, 3).map((p) => p.author).filter(Boolean).join(" · ")}</V>
              </div>
            ) : null}
          </div>
          <SourceLine source={social.source} className="sv-source-plain" />
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}
